var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getComponents, createComponent, updateComponent, toggleActiveComponent, reorderComponents } from '../services/ComponentsService';
import { sortComponentsByPosition, applyOptimisticComponentUpdate, applyOptimisticReorderUpdates } from './hooksHelper';
import { parseAttachableTreeId } from '../utils/attachableTreeIds';
// Query Keys - support filtering by either buildingId or attachableId
export const COMPONENTS_QUERY_KEYS = {
    all: ['components'],
    filter: (filterParams) => [
        'components',
        'filter',
        filterParams.buildingId || 'no-building',
        filterParams.attachableId || 'no-attachable'
    ]
};
// Hook to fetch components filtered by building or attachable
export function useComponentsQuery(filterParams) {
    return useQuery({
        queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getComponents(filterParams);
            if (response.error) {
                throw new Error(response.error.message);
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const data = response.data;
            // Sort components by position recursively
            return Object.assign(Object.assign({}, data), { components: sortComponentsByPosition(data.components) });
        }),
        enabled: !!(filterParams.buildingId || filterParams.attachableId),
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}
// Hook to create a component
export function useCreateComponentMutation(filterParams) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (formData) => __awaiter(this, void 0, void 0, function* () {
            const response = yield createComponent(filterParams, formData);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onSuccess: () => {
            toast.success('Component created successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams)
            });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create component. Please try again.');
        }
    });
}
// Hook to update a component
export function useUpdateComponentMutation(filterParams) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ componentId, updateData }) {
            const response = yield updateComponent(filterParams, componentId, updateData);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ componentId, updateData }) {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            yield queryClient.cancelQueries({ queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams) });
            // Snapshot the previous value
            const previousData = queryClient.getQueryData(COMPONENTS_QUERY_KEYS.filter(filterParams));
            // Optimistically update the cache
            if (previousData) {
                const updatedComponents = applyOptimisticComponentUpdate(previousData.components, componentId, updateData);
                queryClient.setQueryData(COMPONENTS_QUERY_KEYS.filter(filterParams), Object.assign(Object.assign({}, previousData), { components: updatedComponents }));
            }
            // Return the snapshot for potential rollback
            return { previousData };
        }),
        onSuccess: () => {
            toast.success('Component updated successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams)
            });
        },
        onError: (error, variables, context) => {
            toast.error(error.message || 'Failed to update component. Please try again.');
            // If the mutation fails, rollback to the previous data
            if (context === null || context === void 0 ? void 0 : context.previousData) {
                queryClient.setQueryData(COMPONENTS_QUERY_KEYS.filter(filterParams), context.previousData);
            }
        }
    });
}
// Hook to toggle active state of a component (activate/deactivate)
export function useToggleActiveComponentMutation(filterParams) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (componentId) => __awaiter(this, void 0, void 0, function* () {
            const response = yield toggleActiveComponent(filterParams, componentId);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response;
        }),
        onSuccess: () => {
            toast.success('Component status updated successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams)
            });
        },
        onError: (error) => {
            toast.error(`Failed to update component status: ${error.message}`);
        }
    });
}
// Hook to reorder components
export function useReorderComponentsMutation(filterParams) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (dragDropResult) => __awaiter(this, void 0, void 0, function* () {
            const response = yield reorderComponents(filterParams, dragDropResult);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response;
        }),
        onMutate: (dragDropResult) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            yield queryClient.cancelQueries({ queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams) });
            // Snapshot the previous value
            const previousData = queryClient.getQueryData(COMPONENTS_QUERY_KEYS.filter(filterParams));
            // Optimistically update the cache
            if (previousData) {
                const updatedComponents = applyOptimisticReorderUpdates(previousData.components, dragDropResult.positionUpdates.map((update) => {
                    const hasParentId = 'parentId' in update;
                    const hasPosition = 'position' in update;
                    let parentId;
                    let position;
                    if (hasParentId) {
                        if (update.parentId === null) {
                            parentId = null;
                        }
                        else if (parseAttachableTreeId(update.parentId)) {
                            parentId = null;
                        }
                        else {
                            parentId = Number(update.parentId);
                        }
                    }
                    else {
                        parentId = undefined;
                    }
                    if (hasPosition) {
                        position = update.position;
                    }
                    else {
                        position = undefined;
                    }
                    return {
                        id: Number(update.id),
                        position,
                        parentId,
                        attachableId: update.attachableId,
                        attachableType: update.attachableType
                    };
                }));
                queryClient.setQueryData(COMPONENTS_QUERY_KEYS.filter(filterParams), Object.assign(Object.assign({}, previousData), { components: updatedComponents }));
            }
            // Return the snapshot for potential rollback
            return { previousData };
        }),
        onSuccess: () => {
            // Show success feedback
            toast.success('Components reordered successfully!', { duration: 30000 });
            // Invalidate and refetch to ensure server state is up to date
            void queryClient.invalidateQueries({
                queryKey: COMPONENTS_QUERY_KEYS.filter(filterParams)
            });
        },
        onError: (error, variables, context) => {
            // Show error feedback
            toast.error(`Failed to reorder components: ${error.message}`);
            // If the mutation fails, rollback to the previous data
            if (context === null || context === void 0 ? void 0 : context.previousData) {
                queryClient.setQueryData(COMPONENTS_QUERY_KEYS.filter(filterParams), context.previousData);
            }
        }
    });
}
// Combined hook for easier usage
export function useComponents(filterParams) {
    var _a, _b, _c, _d;
    const componentsQuery = useComponentsQuery(filterParams);
    const createComponentMutation = useCreateComponentMutation(filterParams);
    const updateComponentMutation = useUpdateComponentMutation(filterParams);
    const toggleActiveComponentMutation = useToggleActiveComponentMutation(filterParams);
    const reorderComponentsMutation = useReorderComponentsMutation(filterParams);
    return {
        // Query data
        components: ((_a = componentsQuery.data) === null || _a === void 0 ? void 0 : _a.components) || [],
        componentTypes: ((_b = componentsQuery.data) === null || _b === void 0 ? void 0 : _b.componentTypes) || [],
        componentAttachables: ((_c = componentsQuery.data) === null || _c === void 0 ? void 0 : _c.componentAttachables) || [],
        parentComponentAttachables: ((_d = componentsQuery.data) === null || _d === void 0 ? void 0 : _d.parentComponentAttachables) || [],
        // Query state
        isLoading: componentsQuery.isLoading,
        isError: componentsQuery.isError,
        error: componentsQuery.error,
        // Mutations
        createComponent: createComponentMutation.mutateAsync,
        isCreatingComponent: createComponentMutation.isLoading,
        createError: createComponentMutation.error,
        updateComponent: (componentId, field, value) => __awaiter(this, void 0, void 0, function* () {
            return updateComponentMutation.mutateAsync({
                componentId,
                updateData: { [field]: value }
            });
        }),
        isUpdatingComponent: updateComponentMutation.isLoading,
        updateError: updateComponentMutation.error,
        toggleActiveComponent: toggleActiveComponentMutation.mutateAsync,
        isTogglingActive: toggleActiveComponentMutation.isLoading,
        toggleActiveError: toggleActiveComponentMutation.error,
        reorderComponents: reorderComponentsMutation.mutate,
        isReordering: reorderComponentsMutation.isLoading,
        reorderError: reorderComponentsMutation.error,
        // Utility
        refetch: componentsQuery.refetch
    };
}
